<template>
  <v-container>
    <v-row justify="center">
      <ViewHeader :title="viewTitle" />
      <v-col cols="12">
        <v-card class="px-6 py-3" style="min-height: 650px;">
          <template v-if="!loading">
            <v-row justify="space-around">
              <v-col cols="12" lg="6" class="pa-3">
                <v-row no-gutters>
                  <v-col cols="12" class="mb-4">
                    <v-card class="pa-4" outlined>
                      <v-row no-gutters class="d-flex align-center">
                        <v-col cols="4" xl="3" class="px-2">
                          <v-select
                            :items="protocols"
                            v-model="defaultProtocol"
                          ></v-select>
                        </v-col>
                        <v-col cols="5" xl="6" class="px-2">
                          <v-text-field
                            name="urlField"
                            :label="this.$t('message.common.addDomain')"
                            data-vv-name="URL"
                            v-validate.disable="{
                              url: {
                                protocols: ['http', 'https'],
                                require_protocol: false
                              }
                            }"
                            :error-messages="errors.collect('URL')"
                            v-model="websiteUrl"
                            hint="www.twoja-strona.pl"
                            @keyup.enter="addWebsiteToList"
                            :loading="inputLoading"
                          >
                          </v-text-field>
                        </v-col>
                        <v-col cols="3" class="px-2">
                          <v-btn
                            color="primary"
                            block
                            @click="addWebsiteToList"
                            :loading="inputLoading"
                          >
                            {{ $t("message.common.add") }}
                          </v-btn>
                        </v-col>
                        <v-col cols="12" class="info-hint px-2">
                          <p class="grey--text">
                            {{ $t("message.wizard.websitesInfoHint") }}
                          </p>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                  <v-col>
                    <v-card outlined class="pa-4">
                      <v-list class="websites__list scroll-y">
                        <template v-for="website in websites">
                          <v-list-item :key="website.id">
                            <v-list-item-content>
                              <v-list-item-title>
                                <span>{{ website.url }}</span>
                              </v-list-item-title>
                            </v-list-item-content>
                            <v-menu left>
                              <template #activator="{ on }">
                                <v-btn icon v-on="on">
                                  <v-icon color="primary">more_horiz</v-icon>
                                </v-btn>
                              </template>
                              <v-list>
                                <v-list-item>
                                  <v-btn
                                    color="error"
                                    text
                                    @click="deleteFromList(website)"
                                  >
                                    {{ $t("message.common.delete") }}
                                  </v-btn>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </v-list-item>
                        </template>
                      </v-list>
                      <SectionLoader :status="loading" />
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" lg="6" class="pa-3">
                <v-card outlined class="px-4">
                  <v-row class="mt-5">
                    <v-col cols="12" class="mb-3">
                      <h5>Facebook</h5>
                      <div class="d-flex">
                        <v-text-field
                          name="addSocialMediaFacebook"
                          :label="this.$t('message.common.addFbId')"
                          prefix="https://www.facebook.com/"
                          hint="https://www.facebook.com/twoja_nazwa"
                          v-model="facebookProfile"
                          data-vv-name="Facebook"
                          :error-messages="errors.collect('Facebook')"
                          v-validate
                          :loading="loading"
                          :disabled="!isFacebookEditMode"
                        >
                        </v-text-field>
                        <v-btn icon class="mt-3 white">
                          <v-icon @click="updateFacebookField" normal>
                            {{
                              !isFacebookEditMode
                                ? "icon-edit"
                                : "icon-check-bold"
                            }}
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <h5>Instagram</h5>
                      <div class="d-flex">
                        <v-text-field
                          name="addSocialMediaInstagram"
                          :label="this.$t('message.common.addProfileName')"
                          prefix="https://www.instagram.com/"
                          hint="https://www.instagram.com/twoja_nazwa"
                          v-model="instagramProfile"
                          data-vv-name="Instagram"
                          :error-messages="errors.collect('Instagram')"
                          v-validate
                          :loading="loading"
                          :disabled="!isInstagramEditMode"
                        >
                        </v-text-field>
                        <v-btn icon class="mt-3 white">
                          <v-icon normal @click="updateInstagramField">
                            {{
                              !isInstagramEditMode ? "edit" : "icon-check-bold"
                            }}
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </template>
          <template v-if="loading">
            <SectionLoader :status="loading" />
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import companyService from "@/services/company";
import websitesService from "@/services/websites";
import serviceHandler from "@/helpers/handlers/service";
import ViewHeader from "@/components/ui/ViewHeader.vue";

import { checkCompanyId } from "@/helpers/mixins/wizard";

import cuid from "cuid";
import { mapState } from "vuex";

import { regex } from "@/config/regex.js";
import {
  getSocialMediaProfileNameFromUrl,
  trimUrlProtocol
} from "@/helpers/helpers";

export default {
  name: "ProfileWebsites",
  mixins: [checkCompanyId],
  components: {
    SectionLoader,
    ViewHeader
  },
  data() {
    return {
      viewTitle: this.$t("message.wizard.websitesAndSocialMedia"),
      isFacebookEditMode: false,
      isInstagramEditMode: false,
      selectedId: null,
      urlField: null,
      protocols: ["https://", "http://"],
      defaultProtocol: "https://",
      inputLoading: false,
      websites: [],
      facebookProfile: "",
      instagramProfile: "",
      websiteUrl: ""
    };
  },

  watch: {
    facebookProfile(value) {
      if (!value) return;

      this.facebookProfile = getSocialMediaProfileNameFromUrl(
        value,
        regex.facebook
      );
    },

    instagramProfile(value) {
      if (!value) return;

      this.instagramProfile = getSocialMediaProfileNameFromUrl(
        value,
        regex.instagram
      );
    },

    websiteUrl(value) {
      const newVal = value ? trimUrlProtocol(value) : value;

      this.websiteUrl = newVal;
    }
  },

  computed: {
    ...mapState({
      businessAddresses: state => state.businessAddresses.businessAddresses,
      loading: state => state.company.loading,
      companyId: state => state.company.id
    }),

    isFormChanged() {
      return Object.keys(this.fields).some(key => this.fields[key].dirty);
    },

    hasData() {
      return this.businessAddresses.length > 0 || this.websites.length > 0;
    }
  },

  async created() {
    this.$store.commit("websites/SET_LOADING", true);
    await this.$store.dispatch("businessAddresses/getBusinessAddresses");
    await this.fetchWebsites();
    await this.fetchSocialMedia();
    await this.$store.commit("websites/SET_LOADING", false);
  },

  methods: {
    async fetchWebsites() {
      const data = await serviceHandler(websitesService.getWebsitesData());

      if (data.length > 0) {
        this.websites = data;
      }
    },

    async fetchSocialMedia() {
      const data = await serviceHandler(companyService.getCompany());

      if (!data.length > 0) return [];

      const responseData = data[0];

      this.facebookProfile = getSocialMediaProfileNameFromUrl(
        responseData.facebook_url,
        regex.facebook
      );
      this.instagramProfile = getSocialMediaProfileNameFromUrl(
        responseData.instagram_url,
        regex.instagram
      );
    },

    async addWebsiteToList() {
      if (this.websiteUrl) {
        this.urlField = `${this.defaultProtocol}${this.websiteUrl}`;

        const isOnList = this.websites.find(
          website => website.url === this.urlField
        );

        !isOnList ? this.add(this.urlField) : null;
      }
    },

    async add(url) {
      this.inputLoading = true;

      await serviceHandler(websitesService.postWebsites([{ url }]));

      this.fetchWebsites();

      this.websiteUrl = null;
      this.inputLoading = false;
    },

    async deleteFromList(website) {
      const removeFromArr = (arr, websiteId) => {
        const index = arr.findIndex(item => item.id === website.id);
        arr.splice(index, 1);

        return arr;
      };

      await serviceHandler(websitesService.deleteWebsite(website.id));

      removeFromArr(this.websites, website.id);
    },

    validateData() {
      if (!this.hasData) return;
      this.send();
    },

    async updateFacebookField() {
      this.isFacebookEditMode = !this.isFacebookEditMode;

      if (this.isFormChanged) {
        const payload = {
          facebook_url: this.facebookProfile
            ? `https://www.facebook.com/${this.facebookProfile}`
            : ""
        };

        await this.send(payload);

        this.$validator.reset();
      }
    },

    async updateInstagramField() {
      this.isInstagramEditMode = !this.isInstagramEditMode;

      if (this.isFormChanged) {
        const payload = {
          instagram_url: this.instagramProfile
            ? `https://www.instagram.com/${this.instagramProfile}`
            : ""
        };

        await this.send(payload);

        this.$validator.reset();
      }
    },

    send(payload) {
      serviceHandler(companyService.patchCompanyData(payload, this.companyId));
    }
  }
};
</script>

<style lang="scss" scoped>
.websites__list {
  min-height: 370px;
}

.info-hint {
  @include font-size(14px);
}
</style>
